import { ReactNode, useState } from 'react';
import { IconButton, Popover, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useLocalization } from 'localization';
import SearchIcon from '../icons/SearchIcon';
import PlusIcon from '../icons/PlusIcon';
import { colors } from 'theme/mui/colors';
import TextTooltip from '../text/TextTooltip';
import DashboardOrderMaterial from 'modules/v2/dashboard/ordersmaterial/DashboardOrderMaterial';
import LanguageSelection from '../languages/LanguageSelection';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import MenuPopOver from '../shared/MenuPopOver';
import { useAnchor } from 'hooks/general/useAnchor';
import Notifications from 'modules/v2/notification/Notifications';

import NotificationsIcon from '../shared/NotificationsIcon';
import { getNewsApi } from 'api/news/query';
import { userStore } from 'stores/userStore';

type Props = {
  children: ReactNode;
  pageTitle: string;
};

const PageContainer = (props: Props) => {
  const { children, pageTitle } = props;
  const { t, locale } = useLocalization();
  const { toggleModal } = MODALS_STORE.general();
  const { data, isLoading } = getNewsApi(locale);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const languageAnchorEl = useAnchor();
  const orderAnchorEl = useAnchor();
  const notificationAnchorEl = useAnchor();
  const [isClicked, setIsClicked] = useState(false);

  const { user } = userStore();

  return (
    <Stack spacing={0.5} style={{ marginBottom: '1rem', width: '100%', overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" sx={{ padding: '1rem 0' }} alignItems="center">
        <Stack gap={1} direction={'column'}>
          <TextTooltip
            value={t(pageTitle)}
            maxLength={isMobile ? 10 : 50}
            textProps={{
              variant: isMobile ? 'h4' : 'h3',
            }}
          />
          {user?._dto?.isFromEsoft && (
            <Typography variant="caption">{`(${t('common.terms.impersonateId')} ${user?._dto?.currentlyImpersonatingName})`}</Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems={'center'} gap={1}>
          {/* Language Popover */}
          <IconButton onClick={languageAnchorEl.handleOpen}>
            <Stack
              direction="row"
              sx={{
                height: 25,
                width: 25,
                fontSize: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                textTransform: 'uppercase',
              }}
            >
              {locale.toLocaleUpperCase()}
            </Stack>
          </IconButton>
          <IconButton aria-label="search" onClick={() => toggleModal(MODAL_ID.GLOBAL_SEARCH)}>
            <SearchIcon />
          </IconButton>
          <IconButton
            aria-label="notification"
            onClick={event => {
              notificationAnchorEl.handleOpen(event);
              setIsClicked(true);
            }}
          >
            <NotificationsIcon isClicked={isClicked} data={data ?? []} />
          </IconButton>
          <IconButton
            aria-label="quick-create"
            onClick={orderAnchorEl.handleOpen}
            sx={{ borderRadius: '0.5rem', background: colors.specific.buttonBg }}
          >
            <PlusIcon color="white" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack>{children}</Stack>
      <MenuPopOver anchorEl={orderAnchorEl.anchorEl} onClose={orderAnchorEl.handleClose} open={orderAnchorEl.open}>
        <DashboardOrderMaterial />
      </MenuPopOver>
      <MenuPopOver
        anchorEl={notificationAnchorEl.anchorEl}
        onClose={notificationAnchorEl.handleClose}
        open={notificationAnchorEl.open}
      >
        <Notifications data={data ?? []} loading={isLoading} />
      </MenuPopOver>
      <Popover
        open={languageAnchorEl.open}
        anchorEl={languageAnchorEl.anchorEl}
        onClose={languageAnchorEl.handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <LanguageSelection />
      </Popover>
    </Stack>
  );
};

export default PageContainer;
